import React, {useCallback, useMemo, useRef} from 'react';
import {useSelector} from 'react-redux';

import {ProcessFlowchartValidationError} from 'processes/graph-builder-processes/validation/ProcessFlowchartForValidationTypes';
import {BenchlingState} from 'redux-stores/reduceApp';
import {getStore} from 'redux-stores/Store';
import useOnMount from 'ui-library/hooks/useOnMount';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import {SaveState} from 'ui-library/SaveStatusIndicator';

import {StaticTemplateFlowchart} from '../processFlowchartConfigEditorConstants';
import {
  flowchartConfigUpdated,
  flowchartConfigDirtyStateChanged,
  flowchartConfigReadonlyStateChanged,
  flowchartConfigIsSaveInFlightStateChanged,
  flowchartConfigAutosaverPausedChanged,
  flowchartConfigAutosaverBlockedChanged,
  FlowchartConfigAction,
} from './flowchartConfigActions';
import {FlowchartConfigWithMetadata} from './flowchartConfigAutosaveManagerCallbacks';
import {
  FlowchartConfigMetadata,
  FlowchartConfigAutosaverPausedConditionType,
  hasAnyAutosaverPausedConditions,
} from './reduceFlowchartConfig';

export interface LiveStateChanges {
  dirtyState: [boolean, (isDirty: boolean) => void];
  readonlyState: [boolean, (isReadonly: boolean) => void];
  isSaveInFlightState: [boolean, (isSaveInFlight: boolean) => void];
  isAutosaverPausedState: [
    boolean,
    (condition: FlowchartConfigAutosaverPausedConditionType, conditionValue: boolean) => void,
  ];
  isAutosaverBlockedState: [boolean, (isBlocked: boolean) => void];
}

export interface FlowchartConfigStateManagerChildrenProps {
  persistedFlowchartConfig: StaticTemplateFlowchart;
  flowchartConfigMetadata: FlowchartConfigMetadata;
  draftFlowchartConfigAndMetadata: FlowchartConfigWithMetadata;
  draftFlowchartConfigAndMetadataRef: React.MutableRefObject<FlowchartConfigWithMetadata>;
  setFlowchartConfigAndMetadata: (params: {
    flowchartConfigWithMetadata: FlowchartConfigWithPartialMetadata | null;
    skipSettingDirtyState?: boolean;
  }) => void;
  setFlowchartConfigMetadata: (metadata: FlowchartConfigMetadata, skipSettingDirtyState?: boolean) => void;
  liveStateChanges: LiveStateChanges;
}
export interface FlowchartConfigStateManagerProps {
  flowchartConfigId: string;
  persistedFlowchartConfig: StaticTemplateFlowchart;
  flowchartConfigMetadata: FlowchartConfigMetadata;
  children: (props: FlowchartConfigStateManagerChildrenProps) => JSX.Element;
}

function emitAction(action: FlowchartConfigAction): void {
  // Deferring the dispatch ensures we don't trigger a `setState` inside a render cycle
  setTimeout(() => {
    getStore().dispatch(action);
  }, 0);
}

export type FlowchartConfigWithPartialMetadata =
  // Caller can provide one or both of `staticFlowchart` or `flowchartConfigMetadata`
  | {
      staticFlowchart: StaticTemplateFlowchart;
      flowchartConfigMetadata?: Partial<FlowchartConfigMetadata>;
    }
  | {
      staticFlowchart?: StaticTemplateFlowchart;
      flowchartConfigMetadata: Partial<FlowchartConfigMetadata>;
    };

function sendFlowchartConfigDataToRedux(
  flowchartConfigId: string,
  flowchartConfigWithMetadata: FlowchartConfigWithPartialMetadata
): void {
  emitAction(flowchartConfigUpdated({flowchartConfigId, ...flowchartConfigWithMetadata}));
}

function setIsFlowchartConfigDirty(flowchartConfigId: string, isDirty: boolean): void {
  emitAction(flowchartConfigDirtyStateChanged(flowchartConfigId, isDirty));
}

function setIsFlowchartConfigReadonly(flowchartConfigId: string, isReadonly: boolean): void {
  emitAction(flowchartConfigReadonlyStateChanged(flowchartConfigId, isReadonly));
}

function setIsFlowchartConfigSaveInFlight(flowchartConfigId: string, isSaveInFlight: boolean): void {
  emitAction(flowchartConfigIsSaveInFlightStateChanged(flowchartConfigId, isSaveInFlight));
}

function setIsFlowchartConfigAutosaverBlocked(flowchartConfigId: string, isBlocked: boolean): void {
  emitAction(flowchartConfigAutosaverBlockedChanged(flowchartConfigId, isBlocked));
}

function setIsFlowchartConfigAutosaverPaused(
  flowchartConfigId: string,
  condition: FlowchartConfigAutosaverPausedConditionType,
  conditionValue: boolean
): void {
  emitAction(flowchartConfigAutosaverPausedChanged(flowchartConfigId, condition, conditionValue));
}

/**
 * Determines the save state based on whether a save is in flight and whether there are blocking errors.
 */
export function calculateSaveState({
  isSaveInFlight,
  isDirty,
  validationErrors,
}: {
  isSaveInFlight: boolean;
  isDirty: boolean;
  validationErrors: Array<ProcessFlowchartValidationError>;
}): {saveState: SaveState; customSaveStatusMessage?: string} {
  if (isSaveInFlight) {
    return {saveState: SaveState.SAVING};
  }

  const blockingErrors = validationErrors.filter((error) => error.blocksPersistence);
  if (blockingErrors.length > 0) {
    return {saveState: SaveState.UNSAVED, customSaveStatusMessage: 'Resolve errors to save changes'};
  }

  if (isDirty) {
    return {saveState: SaveState.SAVING};
  }

  return {saveState: SaveState.SAVED};
}

/**
 * FlowchartConfigStateManager: manages the draft flowchart config as the user makes edits. Rather than store
 * draft state directly inside a component like FlowchartConfigEditor, we use this component to manage the data in
 * the Redux store. Downstream code doesn't know any better: it can operate as if it were stored in local state.
 */
export function FlowchartConfigStateManager(props: FlowchartConfigStateManagerProps): JSX.Element {
  // Ensure we push the initial version of the flowchart config into Redux. Note that this is async, which is why
  // we have the ?? fallback value immediately below
  useOnMount(() => {
    sendFlowchartConfigDataToRedux(props.flowchartConfigId, {
      staticFlowchart: props.persistedFlowchartConfig,
      flowchartConfigMetadata: props.flowchartConfigMetadata,
    });
  });

  const flowchartConfigData = useSelector(
    (state: BenchlingState) => state.models.flowchartConfigs[props.flowchartConfigId]
  );

  const draftFlowchartConfigAndMetadata: FlowchartConfigWithMetadata = useMemo(
    () => ({
      staticFlowchart: flowchartConfigData?.staticFlowchart ?? props.persistedFlowchartConfig,
      metadata: flowchartConfigData?.flowchartConfigMetadata ?? props.flowchartConfigMetadata,
    }),
    [
      flowchartConfigData?.staticFlowchart,
      flowchartConfigData?.flowchartConfigMetadata,
      props.persistedFlowchartConfig,
      props.flowchartConfigMetadata,
    ]
  );

  // The setDraftFlowchartConfig callback (below) will update the flowchart config kept in redux, where changes will force a
  // re-render. However, we also keep a pointer to the latest flowchart config in a ref. The ref is used when sending data
  // to the server. This protects us from stale closures, as our saves happen in async functions, where state is
  // likely to be stale. Unlike state, refs are updated synchronously, so they're never stale.
  const draftFlowchartConfigAndMetadataRef = useRef<FlowchartConfigWithMetadata>({
    staticFlowchart: props.persistedFlowchartConfig,
    metadata: props.flowchartConfigMetadata,
  });

  const setIsDirty = useCallback(
    (isDirty: boolean) => {
      setIsFlowchartConfigDirty(props.flowchartConfigId, isDirty);
    },
    [props.flowchartConfigId]
  );

  const setFlowchartConfigAndMetadata = useCallback(
    ({
      flowchartConfigWithMetadata,
      skipSettingDirtyState = false,
    }: {
      flowchartConfigWithMetadata: FlowchartConfigWithPartialMetadata | null;
      skipSettingDirtyState?: boolean;
    }): void => {
      // Handle null case - this happens when applyFlowchartChangesCore returns null (no changes)
      if (!flowchartConfigWithMetadata) {
        return;
      }

      const {staticFlowchart, flowchartConfigMetadata} = flowchartConfigWithMetadata;
      // Track if any non-interactive data has changed that should trigger a dirty state and therefore a save
      let changedSomeNonInteractiveData = false;

      if (staticFlowchart != null) {
        draftFlowchartConfigAndMetadataRef.current.staticFlowchart = staticFlowchart;
        changedSomeNonInteractiveData = true;
      }
      if (flowchartConfigMetadata != null) {
        const definedMetadataEntries = Object.entries(flowchartConfigMetadata).filter(
          ([, value]) => value !== undefined
        );

        // Merge the partial metadata, only updating non-undefined fields
        draftFlowchartConfigAndMetadataRef.current.metadata = {
          ...draftFlowchartConfigAndMetadataRef.current.metadata,
          ...Object.fromEntries(definedMetadataEntries),
        };

        const hasNonInteractiveStateChanges = definedMetadataEntries.some(
          ([key]) => key !== 'interactionState'
        );
        if (hasNonInteractiveStateChanges) {
          changedSomeNonInteractiveData = true;
        }
      }

      if (!skipSettingDirtyState && changedSomeNonInteractiveData) {
        setIsDirty(true);
      }
      sendFlowchartConfigDataToRedux(props.flowchartConfigId, flowchartConfigWithMetadata);
    },
    [props.flowchartConfigId, setIsDirty]
  );

  const setFlowchartConfigMetadata = useCallback(
    (metadata: FlowchartConfigMetadata, skipSettingDirtyState: boolean = false): void => {
      setFlowchartConfigAndMetadata({
        flowchartConfigWithMetadata: {flowchartConfigMetadata: metadata},
        skipSettingDirtyState,
      });
    },
    [setFlowchartConfigAndMetadata]
  );

  const setIsReadonly = useCallback(
    (isReadonly: boolean) => {
      setIsFlowchartConfigReadonly(props.flowchartConfigId, isReadonly);
    },
    [props.flowchartConfigId]
  );

  const setIsSaveInFlight = useCallback(
    (isSaveInFlight: boolean) => {
      setIsFlowchartConfigSaveInFlight(props.flowchartConfigId, isSaveInFlight);
    },
    [props.flowchartConfigId]
  );

  const setAutosaverBlocked = useCallback(
    (isBlocked: boolean) => {
      setIsFlowchartConfigAutosaverBlocked(props.flowchartConfigId, isBlocked);
    },
    [props.flowchartConfigId]
  );

  const setAutosaverPaused = useCallback(
    (condition: FlowchartConfigAutosaverPausedConditionType, conditionValue: boolean) => {
      setIsFlowchartConfigAutosaverPaused(props.flowchartConfigId, condition, conditionValue);
    },
    [props.flowchartConfigId]
  );

  const liveStateChanges = useMemo(
    (): LiveStateChanges => ({
      dirtyState: [Boolean(flowchartConfigData?.componentLiveProperties.isDirty), setIsDirty],
      readonlyState: [Boolean(flowchartConfigData?.componentLiveProperties.isReadOnly), setIsReadonly],
      isSaveInFlightState: [
        Boolean(flowchartConfigData?.platformLiveProperties.isSaveInFlight),
        setIsSaveInFlight,
      ],
      isAutosaverPausedState: [
        Boolean(
          hasAnyAutosaverPausedConditions(
            flowchartConfigData?.componentLiveProperties.autosaverPausedConditions ?? new Set()
          )
        ),
        setAutosaverPaused,
      ],
      isAutosaverBlockedState: [
        Boolean(flowchartConfigData?.platformLiveProperties.isAutosaverBlocked),
        setAutosaverBlocked,
      ],
    }),
    [
      flowchartConfigData?.componentLiveProperties.isDirty,
      flowchartConfigData?.componentLiveProperties.isReadOnly,
      flowchartConfigData?.platformLiveProperties.isSaveInFlight,
      flowchartConfigData?.componentLiveProperties.autosaverPausedConditions,
      flowchartConfigData?.platformLiveProperties.isAutosaverBlocked,
      setIsDirty,
      setIsReadonly,
      setIsSaveInFlight,
      setAutosaverPaused,
      setAutosaverBlocked,
    ]
  );

  // Don't render children until Redux has been initialized with the flowchart data
  // This prevents validation from running with incomplete data on initial render.
  // We guarantee that all flowcharts will have at least a start node that can't be deleted.
  const isReduxInitialized =
    flowchartConfigData?.staticFlowchart != null &&
    flowchartConfigData?.staticFlowchart.nodesStaticData.length > 0;

  if (!isReduxInitialized) {
    return <CenteredHorizontalThrobber />;
  }

  return props.children({
    persistedFlowchartConfig: props.persistedFlowchartConfig,
    flowchartConfigMetadata: props.flowchartConfigMetadata,
    draftFlowchartConfigAndMetadata,
    draftFlowchartConfigAndMetadataRef,
    setFlowchartConfigAndMetadata,
    setFlowchartConfigMetadata,
    liveStateChanges,
  });
}
